import React, { useState, useEffect, Suspense } from "react";
import { any, array, bool, func, number, object, string } from "prop-types";
import { Form, Select, Input, Button, Space, Spin } from "antd";
import {
  orderEmailTemplate,
  orderEmailTemplateTiktok,
  reviewEmailTemplate,
} from "@stack/shared";
import TwoComponentSwitch from "../../shared/TwoComponentSwitch/TwoComponentSwitch";
import styles from "./AddNewCampaign.module.css";
import {
  defaultButtonWrapperCol,
  defaultFormItemLayout,
} from "../../../utils/formLayout";
import optionOrNew from "./optionOrNew";
import EmailTemplateVariables from "./EmailTemplateVariables";
import { useEmailTemplatePreview } from "./EmailTemplatePreviewContext";
import { minifyHtml } from "../../../utils/stringUtils";

const EmailTemplateEditor = React.lazy(() => import("./EmailTemplateEditor"));

const FirstForm = ({
  options,
  name,
  loading,
  form,
  onSubmit,
  onBack,
  toggleVisibleComponent,
  setExistingOptionIndex,
}) => {
  const getOptions = () => {
    const optionComponents = [];

    for (let i = 0; i < options.length; i++) {
      const option = options[i];
      const optionValue = JSON.parse(option.value);
      const onNewClick = () => {
        setExistingOptionIndex(i);
        toggleVisibleComponent();
      };
      optionComponents.push(
        optionOrNew({
          option,
          optionData: optionValue,
          idProperty: "emailTemplateId",
          onNewClick,
        })
      );
    }

    return optionComponents;
  };

  const _onSubmit = async () => {
    try {
      await form.validateFields();
      const values = form.getFieldsValue(true);
      onSubmit(values);
    } catch (error) {
      //ignore as Ant Design will handle validation based on form.item rules
    }
  };

  return (
    <Form
      {...defaultFormItemLayout}
      component={false}
      labelAlign="right"
      layout="horizontal"
      name="createTemplateForm"
      form={form}
    >
      <Form.Item
        name={name}
        label="Email Template Name"
        className={styles.formItem}
        rules={[{ required: true, message: "required" }]}
      >
        <Select
          loading={loading}
          showSearch
          className={styles.fullWidth}
          placeholder="Type to search email templates"
          filterOption={(inputValue, option) =>
            option.label.toUpperCase().indexOf(inputValue.toUpperCase()) !== -1
          }
        >
          {getOptions()}
        </Select>
      </Form.Item>
      <Form.Item wrapperCol={defaultButtonWrapperCol} className={styles.mt24}>
        <Space direction="horizontal" size="large">
          <Button type="secondary" onClick={onBack}>
            Back
          </Button>
          <Button onClick={_onSubmit}>Submit</Button>
        </Space>
      </Form.Item>
    </Form>
  );
};

FirstForm.propTypes = {
  options: array,
  name: string,
  loading: bool,
  form: any,
  onSubmit: func.isRequired,
  onBack: func.isRequired,
  toggleVisibleComponent: func,
  setExistingOptionIndex: func,
};

const getDefaultHtmlTemplate = ({
  campaignStepType,
  companyName,
  platform,
  platformReviewHtml,
  steps,
}) => {
  if (campaignStepType === "order") {
    const visibleSteps = steps.filter(s => s.visible);
    const isTiktok = visibleSteps.some(s => s.title === "TikTok Step");
    return isTiktok
      ? orderEmailTemplateTiktok({ tags: ["#ad"], platform })
      : orderEmailTemplate({ tags: ["#ad"], platform });
  }

  if (campaignStepType === "instagram") {
    return orderEmailTemplate({ tags: ["#ad"], platform });
  }

  if (campaignStepType === "tiktok") {
    return orderEmailTemplateTiktok({ tags: ["#ad"], platform });
  }

  // Platforms with their own review template (all but brand website) start from a copy of it, so
  // the default copy lives in one place: the template product_platforms.review_email_template_id
  // points to, which the contract draft flow reuses too.
  if (campaignStepType === "review") {
    return platformReviewHtml || reviewEmailTemplate({ companyName });
  }
};

const SecondForm = ({
  toggleVisibleComponent,
  options,
  setOptions,
  campaignStepName,
  setSelectedOption,
  campaignStepType,
  steps,
  companyName,
  platform,
  existingOptionIndex,
}) => {
  const [form] = Form.useForm();
  const { platformReviewHtml } = useEmailTemplatePreview();
  // The editor's current html, so the placeholder column can list what it uses
  const html = Form.useWatch("html", form);

  useEffect(() => {
    if (existingOptionIndex > -1) {
      const optionData = JSON.parse(options[existingOptionIndex].value);
      form.setFieldsValue(optionData);
    } else {
      form.resetFields();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [existingOptionIndex]);

  const onSubmit = async () => {
    try {
      await form.validateFields();
      const values = form.getFieldsValue(true);
      values.html = minifyHtml(values.html);
      const newOption = {
        label: values.name,
        value: JSON.stringify(values),
      };
      if (existingOptionIndex >= 0) {
        options.splice(existingOptionIndex, 1);
      }
      setOptions([newOption, ...options]);
      setSelectedOption(newOption);
      form.resetFields();
      toggleVisibleComponent();
    } catch (error) {
      //ignore as Ant Design will handle validation based on form.item rules
    }
  };

  const validateTemplateName = value => {
    if (!value) {
      return Promise.reject(new Error("required"));
    }
    if (existingOptionIndex < 0) {
      const existingOption = options.find(o => o.label === value);
      if (existingOption) {
        return Promise.reject(new Error("template name already exists"));
      }
    } else {
      const existingOption = options.find(o => o.label === value);
      const previousExistingOption = options[existingOptionIndex];
      if (
        existingOption &&
        previousExistingOption.label !== existingOption.label
      ) {
        return Promise.reject(new Error("template name already exists"));
      }
    }
    return Promise.resolve();
  };

  return (
    <Suspense fallback={<Spin size="large" />}>
      <Form
        {...defaultFormItemLayout}
        component={false}
        form={form}
        labelAlign="right"
        layout="horizontal"
        name="createTemplateForm"
        initialValues={{
          name: `${campaignStepName} Template`,
          html: getDefaultHtmlTemplate({
            campaignStepType,
            steps,
            companyName,
            platform,
            platformReviewHtml,
          }),
        }}
      >
        <Form.Item
          label="Template Name"
          name="name"
          rules={[
            {
              validator: (rule, value) => validateTemplateName(value),
            },
          ]}
        >
          <Input />
        </Form.Item>

        <div className={styles.emailTemplateField}>
          <Form.Item
            name="html"
            rules={[{ required: true, message: "required" }]}
          >
            <EmailTemplateEditor />
          </Form.Item>
          <EmailTemplateVariables html={html} />
        </div>
        <Form.Item>
          <div className={styles.rightAligned}>
            <Button onClick={onSubmit}>Submit</Button>
          </div>
        </Form.Item>
      </Form>
    </Suspense>
  );
};

SecondForm.propTypes = {
  options: array,
  setSelectedOption: func,
  toggleVisibleComponent: func,
  setOptions: func,
  campaignStepName: string,
  campaignStepType: string,
  steps: array,
  companyName: string,
  platform: string,
  existingOptionIndex: number,
};

function EmailTemplateForm({
  campaignStepName,
  onSubmit,
  name,
  value,
  onBack,
  campaignStepType,
  steps,
  platform,
  companyName,
  emailTemplates,
}) {
  const [options, setOptions] = useState([]);
  const [form] = Form.useForm();
  const [existingOptionIndex, setExistingOptionIndex] = useState(-1);

  useEffect(() => {
    if (emailTemplates) {
      setOptions(
        Object.values(emailTemplates).map(template => ({
          value: JSON.stringify({
            emailTemplateId: template.emailTemplateId,
            name: template.name,
            html: template.html,
          }),
          label: template.name,
        }))
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [emailTemplates]);

  useEffect(() => {
    if (value && name) {
      form.setFieldsValue({ [name]: JSON.stringify(value) });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [name, value]);

  const _onSwitch = newActiveComponent => {
    if (newActiveComponent === 1) {
      setExistingOptionIndex(-1);
    }
  };

  return (
    <TwoComponentSwitch
      className={styles.fullWidth}
      activateFirstText="Select Existing Template"
      activateSecondText="Create New Template"
      onSwitch={_onSwitch}
      firstComponent={
        <FirstForm
          form={form}
          options={options}
          name={name}
          onSubmit={onSubmit}
          onBack={onBack}
          setExistingOptionIndex={setExistingOptionIndex}
        />
      }
      secondComponent={
        <SecondForm
          options={options}
          setOptions={setOptions}
          setSelectedOption={o => {
            form.setFieldsValue({ [name]: o.value });
          }}
          campaignStepName={campaignStepName}
          campaignStepType={campaignStepType}
          steps={steps}
          platform={platform}
          companyName={companyName}
          existingOptionIndex={existingOptionIndex}
        />
      }
    />
  );
}

EmailTemplateForm.propTypes = {
  campaignStepName: string,
  onSubmit: func.isRequired,
  name: string,
  value: object,
  onBack: func,
  campaignStepType: string,
  steps: array,
  platform: string,
  companyName: string,
  emailTemplates: object,
};

export default EmailTemplateForm;
